import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useSessionContext } from '@/contexts/chat/SessionContext'
import { useStorage } from '@/contexts/StorageContext'
import { useThemeMode } from '@/contexts/ThemeContext'
import { useUser } from '@/hooks/useUser'
import useIsMobile from '@/hooks/useIsMobile'
import { api } from '@/lib/api/client'
import { normalizeEmojis } from '@/lib/emojiUtils'
import { theme } from '@/lib/theme'
import { cacheThemeJson, saveSelectedIdentity, saveSelectedThemeId } from '@/types/UserSettings'
import Button from './ui/Button'
import Container from './ui/Container'
import Message from './ui/Message'
import Modal from './ui/Modal'
import { FileExplorerHelpProvider, HelpToggle, HelpIcon, useHelp } from './helpers/FileExplorerHelp'
import GoogleFontSelector from './GoogleFontSelector'

interface Item {
  id: string
  name: string
  updatedAt: string
  type: string
  size: string
  icon: string
  // Marketplace metadata
  shareId?: string
  marketplaceShareId?: string
  isListed?: boolean
  listingId?: string
  price?: number
  purchaseCount?: number
  viewCount?: number
  importCount?: number
  importedFromShareId?: string
}

interface Folder {
  name: string
  path: string
  items: Item[]
  children?: Folder[]
}

interface FileExplorerProps {
  extraData?: {
    initialFolder?: string
  }
  onActivateWindow?: () => void
  onIdentitySelect?: () => void
}

/**
 * File explorer component for browsing and managing files and folders.
 * Provides a sidebar for folder navigation and main content area for file listings.
 */
function FileExplorerContent({ extraData, onActivateWindow, onIdentitySelect }: FileExplorerProps) {
  const { helpEnabled } = useHelp()
  const { t } = useTranslation()
  const {
    artifacts,
    isLoading,
    openArtifact,
    deleteArtifact,
    fetchArtifacts,
    themes,
    fetchThemes,
    identities,
    fetchIdentities,
    deleteIdentity,
  } = useStorage()
  const { colors } = useThemeMode()
  const { switchSession } = useSessionContext()
  const { user } = useUser()
  const isMobile = useIsMobile()
  const containerRef = useRef<HTMLDivElement>(null)
  const [currentFolder, setCurrentFolder] = useState(
    extraData?.initialFolder || '/'
  )
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set(['/'])
  )
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<Item | null>(null)
  const [isCompact, setIsCompact] = useState(false)
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [itemToShare, setItemToShare] = useState<Item | null>(null)
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [isSharing, setIsSharing] = useState(false)
  const [shareCopyFeedback, setShareCopyFeedback] = useState(false)
  const [copiedItemId, setCopiedItemId] = useState<string | null>(null)
  const [marketplaceListings, setMarketplaceListings] = useState<any[]>([])
  const [isLoadingMarketplace, setIsLoadingMarketplace] = useState(false)
  const [showMobileToast, setShowMobileToast] = useState(false)
  const mobileToastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clear mobile toast timeout on unmount
  useEffect(() => {
    return () => {
      if (mobileToastTimeoutRef.current) {
        clearTimeout(mobileToastTimeoutRef.current)
      }
    }
  }, [])

  // Helper to construct share URL
  const getShareUrl = useCallback((item: Item) => {
    if (!item.shareId) return ''
    const typeSegment =
      item.type === 'html'
        ? 'artifacts'
        : item.type === 'theme'
        ? 'themes'
        : 'identities'
    return `${window.location.origin}/${typeSegment}/shared/${item.shareId}`
  }, [])

  const getTweetUrl = useCallback((item: Item, url: string) => {
    const typeName =
      item.type === 'html'
        ? 'artifact'
        : item.type === 'theme'
        ? 'theme'
        : 'identity'
    const text = `I just created this ${typeName} on honcho.chat: ${url}`
    return `https://twitter.com/intent/post?text=${encodeURIComponent(text)}`
  }, [])

  // Helper function to format bytes (memoized to prevent infinite loops)
  const formatBytes = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${Math.round(bytes / k ** i)} ${sizes[i]}`
  }, [])

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString)
    const formattedDate = date.toLocaleDateString()
    const formattedTime = date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })
    return `${formattedDate} ${formattedTime}`
  }

  // Fetch marketplace listings for the current user
  useEffect(() => {
    const fetchMarketplaceListings = async () => {
      if (!user) return

      setIsLoadingMarketplace(true)
      try {
        const { data, error } = await api.api.marketplace['my-listings'].get()
        if (!error && data?.listings) {
          setMarketplaceListings(data.listings)
        }
      } catch (error) {
        console.error('Error fetching marketplace listings:', error)
      } finally {
        setIsLoadingMarketplace(false)
      }
    }

    fetchMarketplaceListings()
  }, [user, artifacts, themes, identities])

  // Helper to find marketplace listing for an item
  const getListingForItem = useCallback((itemId: string, itemType: string) => {
    return marketplaceListings.find(
      (listing) => listing.item_id === itemId && listing.item_type === itemType
    )
  }, [marketplaceListings])

  // Convert themes from context to Item format (reverse so newest appear at bottom)
  const toolThemes: Item[] = themes.slice().reverse().map((theme) => {
    const listing = getListingForItem(theme.id, 'theme')
    return {
      id: theme.id,
      name: theme.name,
      updatedAt: theme.updatedAt,
      type: 'theme',
      size: formatBytes(theme.size),
      icon: '🎨',
      shareId: theme.shareId,
      isListed: !!listing,
      listingId: listing?.id,
      price: listing?.price_usd,
      purchaseCount: listing?.purchase_count || 0,
      viewCount: theme.viewCount || 0,
      importCount: theme.importCount || 0,
      importedFromShareId: theme.importedFromShareId,
    }
  })

  // Convert identities from context to Item format
  const toolIdentities: Item[] = identities.slice().reverse().map((identity) => {
    const listing = getListingForItem(identity.id, 'identity')
    return {
      id: identity.id,
      name: identity.name,
      updatedAt: identity.updatedAt,
      type: 'identity',
      size: formatBytes(identity.size),
      icon: '👤',
      shareId: identity.shareId,
      isListed: !!listing,
      listingId: listing?.id,
      price: listing?.price_usd,
      purchaseCount: listing?.purchase_count || 0,
      viewCount: identity.viewCount || 0,
      importCount: identity.importCount || 0,
      importedFromShareId: identity.importedFromShareId,
    }
  })

  // Built-in default identity
  const builtInIdentities: Item[] = [
    {
      id: 'builtin-default',
      name: 'default.json',
      updatedAt: new Date().toISOString(),
      type: 'identity',
      size: '0 KB',
      icon: '🏠',
    },
  ]

  // Local built-in themes (fallback / pre-seeded)
  const builtInThemes: Item[] = [
    {
      id: 'builtin-reset',
      name: 'default.json',
      updatedAt: new Date().toISOString(),
      type: 'theme',
      size: formatBytes(0),
      icon: '🧹',
    },
    {
      id: 'builtin-yousim',
      name: 'Yousim_theme.json',
      updatedAt: new Date().toISOString(),
      type: 'theme',
      size: formatBytes(0),
      icon: '🎨',
    },
    // {
    //   id: 'builtin-bubble',
    //   name: 'bubble_gum_lime.json',
    //   updatedAt: new Date().toISOString(),
    //   type: 'theme',
    //   size: formatBytes(0),
    //   icon: '🎨',
    // },
  ]

  // Convert artifacts to folder structure (group themes and identities separately)
  const folders: Folder[] = [
    {
      name: t('fileExplorer.folders.root'),
      path: '/',
      items: [],
      children: [
        {
          name: t('fileExplorer.folders.artifacts'),
          path: '/Artifacts',
          items: artifacts
            .filter((a) => a.type !== 'theme')
            .map((artifact) => {
              const listing = getListingForItem(artifact.id, 'artifact')
              return {
                id: artifact.id,
                name: artifact.name,
                updatedAt: artifact.updatedAt,
                type: 'html',
                size: formatBytes(artifact.size),
                icon: '🌐',
                shareId: artifact.shareId,
                isListed: !!listing,
                listingId: listing?.id,
                price: listing?.price_usd,
                purchaseCount: listing?.purchase_count || 0,
                viewCount: artifact.viewCount || 0,
                importCount: artifact.importCount || 0,
                importedFromShareId: artifact.importedFromShareId,
              }
            }),
          children: [],
        },
        {
          name: t('fileExplorer.folders.themes'),
          path: '/Themes',
          items: [...builtInThemes, ...toolThemes],
          children: [],
        },
        {
          name: t('fileExplorer.folders.identities'),
          path: '/Identities',
          items: [...builtInIdentities, ...toolIdentities],
          children: [],
        },
      ],
    },
  ]

  const openItem = useCallback((item: Item) => {
    if (item.type === 'identity') {
      // Haptic feedback on mobile
      if (isMobile && navigator.vibrate) {
        navigator.vibrate([10, 30, 10]) // Short double tap pattern
      }
      
      // Handle built-in default identity
      if (item.id === 'builtin-default') {
        // Clear selected identity and switch to default session
        saveSelectedIdentity('default')
        window.dispatchEvent(new CustomEvent('identity-changed'))
        switchSession('default')
        // Open chat window on mobile
        onIdentitySelect?.()
        return
      }

      // Select identity and switch to its session
      const identity = identities.find((i) => i.id === item.id)
      if (identity) {
        // Extract identity name (remove .json extension)
        const identityName = identity.name.replace(/\.json$/, '')

        // Save the selected identity ID
        saveSelectedIdentity(identityName)
        window.dispatchEvent(new CustomEvent('identity-changed'))

        // Switch to the session for this identity
        switchSession(identityName)
        
        // Open chat window on mobile
        onIdentitySelect?.()
      }
      return
    }
    if (item.type === 'theme') {
      // Apply on click rather than opening a window
      const run = async () => {
        if (item.id.startsWith('builtin-')) {
          try {
            const m = await import('@/lib/theme')
            if (item.id === 'builtin-reset') {
              m.resetThemeGlobally()
              localStorage.removeItem('kyogre-cached-theme')
              saveSelectedThemeId('')
            }
            // else if (item.id === 'builtin-bubble') {
            //   // Clear any previously set custom CSS vars before applying bubblegum
            //   m.resetThemeGlobally()
            //   const bubbleTheme = {
            //     light: {
            //       colors: {
            //         primaryBg: '#fff0f6',
            //         secondaryBg: '#f0fdf4',
            //         terminalBg: '#ffffff',
            //         headerBg: '#ffe4ef',
            //         primaryBorder: '#fbcfe8',
            //         secondaryBorder: '#bbf7d0',
            //         headerBorder: '#fbcfe8',
            //         primaryText: '#111827',
            //         secondaryText: '#334155',
            //         tertiaryText: '#475569',
            //         primaryAccent: '#22c55e',
            //         visualAccent2: '#22c55e',
            //         visualAccent1: '#ec4899',
            //         assistantBg: '#fff5f9',
            //         systemMsgBg: '#fdf2f8',
            //         userBg: '#eefcf2',
            //         userMsgBg: '#dcfce7',
            //         assistantMsgBg: 'rgba(34, 197, 94, 0.08)',
            //         assistantMsgGlow: 'rgba(34, 197, 94, 0.15)',
            //         assistantMsgAccent: '#22c55e',
            //         userMsgBgAlpha: 'rgba(34, 197, 94, 0.12)',
            //         userMsgGlow: 'rgba(34, 197, 94, 0.15)',
            //         userMsgAccent: '#22c55e',
            //         scrollbarThumb: '#a3a3a3',
            //         scrollbarTrack: '#f4f4f5',
            //         folderSelectedBg: '#22c55e',
            //         folderSelectedText: '#FFFFFF',
            //         emptyStateText: '#6B7280',
            //       },
            //     },
            //     dark: {
            //       colors: {
            //         primaryBg: '#18101a',
            //         secondaryBg: '#112616',
            //         terminalBg: '#18101a',
            //         headerBg: '#1f1321',
            //         primaryBorder: '#3f3f46',
            //         secondaryBorder: '#1d3a24',
            //         headerBorder: '#3f3f46',
            //         primaryText: '#f8fafc',
            //         secondaryText: '#e2e8f0',
            //         tertiaryText: '#94a3b8',
            //         primaryAccent: '#84cc16',
            //         visualAccent2: '#84cc16',
            //         visualAccent1: '#ff79c6',
            //         assistantBg: '#241a26',
            //         systemMsgBg: '#1e1520',
            //         userBg: '#1c2e20',
            //         userMsgBg: '#204127',
            //         assistantMsgBg: 'rgba(132, 204, 22, 0.08)',
            //         assistantMsgGlow: 'rgba(132, 204, 22, 0.15)',
            //         assistantMsgAccent: '#84cc16',
            //         userMsgBgAlpha: 'rgba(132, 204, 22, 0.12)',
            //         userMsgGlow: 'rgba(132, 204, 22, 0.15)',
            //         userMsgAccent: '#84cc16',
            //         scrollbarThumb: '#71717a',
            //         scrollbarTrack: '#262626',
            //         folderSelectedBg: '#84cc16',
            //         folderSelectedText: '#FFFFFF',
            //         emptyStateText: '#9CA3AF',
            //       },
            //     },
            //   }
            //   m.applyThemeGlobally(bubbleTheme)
            //   cacheThemeJson(bubbleTheme)
            //   saveSelectedThemeId(item.id)
            // }
            else if (item.id === 'builtin-yousim') {
              // Clear any previously set custom CSS vars before applying Yousim theme
              m.resetThemeGlobally()
              const yousimTheme = {
                light: {
                  colors: {
                    primaryBg: '#FDF6E3',
                    secondaryBg: '#EEE8D5',
                    terminalBg: '#FDF6E3',
                    headerBg: '#EEE8D5',
                    primaryBorder: '#2AA199',
                    secondaryBorder: '#258BD2',
                    headerBorder: '#2AA199',
                    terminalBorder: '#D43683',
                    primaryText: '#2AA199',
                    secondaryText: '#258BD2',
                    tertiaryText: '#6C71C4',
                    darkText: '#073642',
                    lightText: '#FDF6E3',
                    textUser: '#2AA199',
                    headerText: '#2AA199',
                    secondaryHeaderText: '#D43683',
                    visualAccent1: '#258BD2',
                    visualAccent2: '#2AA199',
                    visualAccent3: '#CC4F1A',
                    visualAccent4: '#D43683',
                    primaryAccent: '#2AA199',
                    hoverAccent: '#258BD2',
                    successBg: 'rgba(42, 161, 153, 0.15)',
                    successBorder: '#2AA199',
                    errorBg: 'rgba(204, 79, 26, 0.15)',
                    errorBorder: '#CC4F1A',
                    assistantBg: '#FDF6E3',
                    systemMsgBg: '#EEE8D5',
                    userBg: '#FDF6E3',
                    userMsgBg: '#EEE8D5',
                    assistantMsgBg: 'rgba(42, 161, 153, 0.18)',
                    assistantMsgGlow: 'rgba(42, 161, 153, 0.25)',
                    assistantMsgAccent: '#2AA199',
                    userMsgBgAlpha: 'rgba(108, 113, 196, 0.18)',
                    userMsgGlow: 'rgba(212, 54, 131, 0.25)',
                    userMsgAccent: '#6C71C4',
                    scrollbarThumb: '#2AA199',
                    scrollbarTrack: '#EEE8D5',
                    gridOverlay: 'rgba(0,0,0,0.02)',
                    white: '#FFFFFF',
                    gridLine: 'rgba(42, 161, 153, 0.1)',
                    windowBg: '#FDF6E3',
                    windowBgAlpha: 'rgba(253, 246, 227, 0.95)',
                    windowBorder: '#2AA199',
                    windowBorderActive: '#D43683',
                    windowHeaderBg: '#EEE8D5',
                    windowHeaderBorder: '#2AA199',
                    windowControlBg: '#FDF6E3',
                    windowControlBgHover: 'rgba(212, 54, 131, 0.1)',
                    navBg: '#EEE8D5',
                    navBorder: '#2AA199',
                    navTabBgActive: '#D6E4F5',
                    navTabBgHover: '#C7E9E6',
                    mobileNavBg: '#EEE8D5',
                    mobileNavBorder: '#D43683',
                    mobileNavIconHover: 'rgba(212, 54, 131, 0.15)',
                    mobileNavIconActive: '#2AA199',
                    folderSelectedBg: '#2AA199',
                    folderSelectedText: '#FDF6E3',
                    emptyStateText: '#93A1A1',
                    webIndicatorOnBg: 'rgba(42, 161, 153, 0.15)',
                    webIndicatorOnBorder: 'rgba(42, 161, 153, 0.4)',
                    webIndicatorOffBg: 'rgba(204, 79, 26, 0.1)',
                    webIndicatorOffBorder: '#CC4F1A',
                    orbListening: 'rgba(42, 161, 153, 0.5)',
                    orbSpeaking: '#2AA199',
                    orbProcessing: '#D43683',
                    orbListeningGlow: 'rgba(42, 161, 153, 0.8)',
                    orbSpeakingGlow: 'rgba(42, 161, 153, 1)',
                    orbProcessingGlow: 'rgba(212, 54, 131, 0.8)',
                    orbDefaultGlow: 'rgba(42, 161, 153, 0.2)',
                  },
                },
                dark: {
                  colors: {
                    primaryBg: '#002b36',
                    secondaryBg: '#073642',
                    terminalBg: '#002b36',
                    headerBg: '#073642',
                    primaryBorder: '#2AA199',
                    secondaryBorder: '#268BD2',
                    headerBorder: '#2AA199',
                    terminalBorder: '#D43683',
                    primaryText: '#2AA199',
                    secondaryText: '#268BD2',
                    tertiaryText: '#6C71C4',
                    darkText: '#002b36',
                    lightText: '#FDF6E3',
                    textUser: '#2AA199',
                    headerText: '#2AA199',
                    secondaryHeaderText: '#D43683',
                    visualAccent1: '#268BD2',
                    visualAccent2: '#2AA199',
                    visualAccent3: '#CC4F1A',
                    visualAccent4: '#D43683',
                    primaryAccent: '#2AA199',
                    hoverAccent: '#268BD2',
                    successBg: 'rgba(42, 161, 153, 0.15)',
                    successBorder: '#2AA199',
                    errorBg: 'rgba(204, 79, 26, 0.15)',
                    errorBorder: '#CC4F1A',
                    assistantBg: '#073642',
                    systemMsgBg: '#002b36',
                    userBg: '#073642',
                    userMsgBg: '#073642',
                    assistantMsgBg: 'rgba(42, 161, 153, 0.18)',
                    assistantMsgGlow: 'rgba(42, 161, 153, 0.25)',
                    assistantMsgAccent: '#2AA199',
                    userMsgBgAlpha: 'rgba(108, 113, 196, 0.18)',
                    userMsgGlow: 'rgba(212, 54, 131, 0.25)',
                    userMsgAccent: '#6C71C4',
                    scrollbarThumb: '#2AA199',
                    scrollbarTrack: '#073642',
                    gridOverlay: 'rgba(255,255,255,0.02)',
                    white: '#FFFFFF',
                    gridLine: 'rgba(42, 161, 153, 0.15)',
                    windowBg: '#073642',
                    windowBgAlpha: 'rgba(7, 54, 66, 0.95)',
                    windowBorder: '#2AA199',
                    windowBorderActive: '#D43683',
                    windowHeaderBg: '#002b36',
                    windowHeaderBorder: '#2AA199',
                    windowControlBg: '#2AA199',
                    windowControlBgHover: 'rgba(212, 54, 131, 0.2)',
                    navBg: '#073642',
                    navBorder: '#2AA199',
                    navTabBgActive: '#60A5FA',
                    navTabBgHover: '#0B4D4A',
                    mobileNavBg: '#073642',
                    mobileNavBorder: '#D43683',
                    mobileNavIconHover: 'rgba(212, 54, 131, 0.2)',
                    mobileNavIconActive: '#2AA199',
                    folderSelectedBg: '#2AA199',
                    folderSelectedText: '#002b36',
                    emptyStateText: '#586e75',
                    webIndicatorOnBg: 'rgba(42, 161, 153, 0.2)',
                    webIndicatorOnBorder: 'rgba(42, 161, 153, 0.5)',
                    webIndicatorOffBg: 'rgba(204, 79, 26, 0.15)',
                    webIndicatorOffBorder: '#CC4F1A',
                    orbListening: 'rgba(42, 161, 153, 0.5)',
                    orbSpeaking: '#2AA199',
                    orbProcessing: '#D43683',
                    orbListeningGlow: 'rgba(42, 161, 153, 0.8)',
                    orbSpeakingGlow: 'rgba(42, 161, 153, 1)',
                    orbProcessingGlow: 'rgba(212, 54, 131, 0.8)',
                    orbDefaultGlow: 'rgba(42, 161, 153, 0.3)',
                  },
                },
              }
              m.applyThemeGlobally(yousimTheme)
              cacheThemeJson(yousimTheme)
              saveSelectedThemeId(item.id)
            }
          } catch (error) {
            console.error('Error applying built-in theme:', error)
          }
        } else {
          // Try themes API first (for tool-created themes)
          const { data: themeData, error: themeError } =
            await api.api.themes({ id: item.id }).get()

          if (!themeError && themeData?.theme) {
            const json = JSON.parse(themeData.theme.content)
            const m = await import('@/lib/theme')
            m.applyThemeGlobally(json)
            cacheThemeJson(json)
            saveSelectedThemeId(item.id)
          } else {
            // Fallback to artifacts API (for legacy themes)
            const { data: artifactData } = await api.api.artifacts({ id: item.id }).get()
            if (artifactData?.artifact) {
              const json = JSON.parse(artifactData.artifact.content)
              const m = await import('@/lib/theme')
              m.applyThemeGlobally(json)
              cacheThemeJson(json)
              saveSelectedThemeId(item.id)
            } else {
              console.error('Failed to load theme from both APIs')
            }
          }
        }
      }
      run()
      return
    }
    
    // On mobile, show toast instead of opening artifact
    if (isMobile && (item.type === 'html' || item.type === 'artifact')) {
      setShowMobileToast(true)
      if (mobileToastTimeoutRef.current) {
        clearTimeout(mobileToastTimeoutRef.current)
      }
      mobileToastTimeoutRef.current = setTimeout(() => setShowMobileToast(false), 3000)
      return
    }
    
    openArtifact(item.id)
  }, [identities, openArtifact, switchSession, isMobile])

  const openFolder = (folderPath: string) => {
    setCurrentFolder(folderPath)
  }

  const handleOpenItem = useCallback((
    item: Item & { isFolder?: boolean; path?: string }
  ) => {
    onActivateWindow?.()
    if (item.type === 'folder' || item.isFolder) {
      if (item.path) {
        openFolder(item.path)
      }
    } else {
      openItem(item)
    }
  }, [openItem, onActivateWindow])

  const handleDeleteClick = (item: Item, event: React.MouseEvent) => {
    event.stopPropagation()
    onActivateWindow?.()
    setItemToDelete(item)
    setDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      if (
        itemToDelete.type === 'theme' &&
        !itemToDelete.id.startsWith('builtin-')
      ) {
        // Delete tool theme
        try {
          const { error: deleteError } = await api.api.themes({ id: itemToDelete.id }).delete()
          if (!deleteError) {
            // Refresh themes list
            await fetchThemes()
          }
        } catch (error) {
          console.error('Error deleting theme:', error)
        }
      } else if (itemToDelete.type === 'identity') {
        // Delete identity
        await deleteIdentity(itemToDelete.id)
      } else {
        // Delete artifact
        await deleteArtifact(itemToDelete.id)
      }
      setDeleteModalOpen(false)
      setItemToDelete(null)
    }
  }

  const handleCancelDelete = () => {
    setDeleteModalOpen(false)
    setItemToDelete(null)
  }

  const handleShareClick = async (item: Item, event: React.MouseEvent) => {
    event.stopPropagation()
    onActivateWindow?.()
    setItemToShare(item)
    setShareModalOpen(true)
    setIsSharing(true)
    setShareUrl(null)

    try {
      if (item.type === 'theme') {
        const { data, error } = await api.api.themes.share.post({ themeId: item.id })
        if (error) {
          console.error('Error sharing theme:', error)
          setShareModalOpen(false)
          return
        }
        setShareUrl(data?.url || '')
        // Refresh themes to get updated shareId
        await fetchThemes()
      } else if (item.type === 'html') {
        const { data, error } = await api.api.artifacts.share.post({ artifactId: item.id })
        if (error) {
          console.error('Error sharing artifact:', error)
          setShareModalOpen(false)
          return
        }
        setShareUrl(data?.url || '')
        // Refresh artifacts to get updated shareId
        await fetchArtifacts()
      } else if (item.type === 'identity') {
        const { data, error } = await api.api.identities.share.post({ identityId: item.id })
        if (error) {
          console.error('Error sharing identity:', error)
          setShareModalOpen(false)
          return
        }
        setShareUrl(data?.url || '')
        // Refresh identities to get updated shareId
        await fetchIdentities()
      }
    } catch (error) {
      console.error('Error creating share link:', error)
      setShareModalOpen(false)
    } finally {
      setIsSharing(false)
    }
  }

  const handleCopyShareLink = async () => {
    if (!shareUrl) return
    onActivateWindow?.()

    try {
      await navigator.clipboard.writeText(shareUrl)
      setShareCopyFeedback(true)
      setTimeout(() => setShareCopyFeedback(false), 2000)
    } catch (err) {
      console.error('Error copying to clipboard:', err)
    }
  }

  const handleCloseShareModal = () => {
    setShareModalOpen(false)
    setItemToShare(null)
    setShareUrl(null)
    setShareCopyFeedback(false)
  }

  const handleUnshareClick = async (item: Item, event: React.MouseEvent) => {
    event.stopPropagation()
    onActivateWindow?.()

    // Confirm with user
    if (!window.confirm(`Remove public share link for "${item.name}"? This will make it private again.`)) {
      return
    }

    try {
      if (item.type === 'theme') {
        const { error } = await api.api.themes.unshare({ id: item.id }).delete()
        if (error) {
          console.error('Error unsharing theme:', error)
          alert('Failed to unshare theme')
          return
        }
        await fetchThemes()
      } else if (item.type === 'html') {
        const { error } = await api.api.artifacts.unshare({ id: item.id }).delete()
        if (error) {
          console.error('Error unsharing artifact:', error)
          alert('Failed to unshare artifact')
          return
        }
        await fetchArtifacts()
      } else if (item.type === 'identity') {
        const { error } = await api.api.identities.unshare({ id: item.id }).delete()
        if (error) {
          console.error('Error unsharing identity:', error)
          alert('Failed to unshare identity')
          return
        }
        await fetchIdentities()
      }
    } catch (error) {
      console.error('Error unsharing item:', error)
      alert('Failed to unshare item')
    }
  }

  const getAsciiArt = () => {
    const text = t('fileExplorer.noFilesFound')
    const lines = text.split('\n')
    const line1 = lines[0] || 'No Files'
    const line2 = lines[1] || 'Found'

    // Calculate visual width: Japanese/full-width chars = 2, others = 1
    const getVisualWidth = (str: string) => {
      return Array.from(str).reduce((width, char) => {
        const code = char.charCodeAt(0)
        // Full-width characters (Japanese, Chinese, etc.)
        if ((code >= 0x3000 && code <= 0x9FFF) ||
            (code >= 0xFF00 && code <= 0xFFEF) ||
            (code >= 0xAC00 && code <= 0xD7AF)) {
          return width + 2
        }
        return width + 1
      }, 0)
    }

    const boxWidth = 16 // Width of the box content area
    const line1Width = getVisualWidth(line1)
    const line2Width = getVisualWidth(line2)

    // Calculate padding (half-width spaces)
    const pad1 = Math.max(0, Math.floor((boxWidth - line1Width) / 2))
    const pad2 = Math.max(0, Math.floor((boxWidth - line2Width) / 2))

    const padding1 = ' '.repeat(pad1)
    const padding2 = ' '.repeat(pad2)
    const trailing1 = ' '.repeat(Math.max(0, boxWidth - line1Width - pad1))
    const trailing2 = ' '.repeat(Math.max(0, boxWidth - line2Width - pad2))

    return `
╭────────────────╮
│${padding1}${line1}${trailing1}│
│${padding2}${line2}${trailing2}│
╰────────────────╯
    `
  }

  const findFolderByPath = (folders: Folder[], path: string): Folder | null => {
    for (const folder of folders) {
      if (folder.path === path) {
        return folder
      }
      if (folder.children) {
        const found = findFolderByPath(folder.children, path)
        if (found) return found
      }
    }
    return null
  }

  /**
   * Recursively finds the most recent updatedAt date from all items within a folder and its subfolders.
   * Returns null if no items are found.
   */
  const getMostRecentDateInFolder = (folder: Folder): string | null => {
    let mostRecentDate: string | null = null

    // Check all items in the current folder
    if (folder.items && folder.items.length > 0) {
      for (const item of folder.items) {
        if (
          !mostRecentDate ||
          new Date(item.updatedAt) > new Date(mostRecentDate)
        ) {
          mostRecentDate = item.updatedAt
        }
      }
    }

    // Recursively check all child folders
    if (folder.children) {
      for (const childFolder of folder.children) {
        const childMostRecent = getMostRecentDateInFolder(childFolder)
        if (
          childMostRecent &&
          (!mostRecentDate ||
            new Date(childMostRecent) > new Date(mostRecentDate))
        ) {
          mostRecentDate = childMostRecent
        }
      }
    }

    return mostRecentDate
  }

  const getFolderContents = (folderPath: string) => {
    const folder = findFolderByPath(folders, folderPath)
    if (!folder) return []

    const items = folder.items || []
    const subfolders = (folder.children || []).map((child) => {
      const mostRecentDate = getMostRecentDateInFolder(child)
      return {
        id: `folder-${child.path}`,
        name: child.name,
        updatedAt: mostRecentDate || '-',
        type: 'folder',
        size: '-',
        icon: '📁',
        isFolder: true,
        path: child.path,
      }
    })

    return [...subfolders, ...items]
  }

  const toggleFolder = (folderPath: string) => {
    setExpandedFolders((prev) => {
      const newExpanded = new Set(prev)
      if (newExpanded.has(folderPath)) {
        newExpanded.delete(folderPath)
      } else {
        newExpanded.add(folderPath)
      }
      return newExpanded
    })
  }

  const renderFolder = (
    folder: Folder,
    depth: number = 0
  ): React.JSX.Element => {
    const isExpanded = expandedFolders.has(folder.path)
    const hasChildren = folder.children && folder.children.length > 0
    const indentSize = depth * 20

    return (
      <div key={folder.path}>
        <div
          style={{
            ...folderItemStyles.base,
            ...(currentFolder === folder.path ? folderItemStyles.active : {}),
            paddingLeft: `${parseInt(theme.spacing.sm, 10) + indentSize}px`,
            alignItems: 'center',
          }}
          {...(folder.path === '/Artifacts' ? { 'data-folder-artifacts': true } : {})}
          {...(folder.path === '/Themes' ? { 'data-folder-themes': true } : {})}
          {...(folder.path === '/Identities' ? { 'data-folder-identities': true } : {})}
          onClick={(e) => {
            e.stopPropagation()
            onActivateWindow?.()
            setCurrentFolder(folder.path)
          }}
          onMouseEnter={(e) => {
            if (currentFolder !== folder.path) {
              Object.assign(e.currentTarget.style, folderItemStyles.hover)
            }
          }}
          onMouseLeave={(e) => {
            if (currentFolder !== folder.path) {
              e.currentTarget.style.backgroundColor = 'transparent'
              e.currentTarget.style.color = colors.primaryText
            }
          }}
        >
          {hasChildren && (
            <span
              style={{
                marginRight: theme.spacing.xs,
                cursor: 'pointer',
                fontSize: '12px',
                userSelect: 'none',
                width: '12px',
                textAlign: 'center',
              }}
              onClick={(e) => {
                e.stopPropagation()
                onActivateWindow?.()
                toggleFolder(folder.path)
              }}
            >
              {isExpanded ? '▼' : '▶'}
            </span>
          )}
          {!hasChildren && (
            <span
              style={{ width: '12px', marginRight: theme.spacing.xs }}
            ></span>
          )}
          <span style={{ marginRight: theme.spacing.sm, fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('📁')}</span>
          {folder.name}
          {folder.path === '/Artifacts' && <HelpIcon text={t('help.fileExplorer.artifactsFolder')} position="right" />}
          {folder.path === '/Themes' && <HelpIcon text={t('help.fileExplorer.themesFolder')} position="right" />}
          {folder.path === '/Identities' && <HelpIcon text={t('help.fileExplorer.identitiesFolder')} position="right" />}
        </div>
        {isExpanded && (
          <div>
            {/* Render child folders */}
            {folder.children?.map((childFolder) =>
              renderFolder(childFolder, depth + 1)
            )}
          </div>
        )}
      </div>
    )
  }

  // Measure container width to decide compact mode (good for small windows inside desktop)
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width
      // Widen breakpoint so left-half layouts also use compact view
      setIsCompact(width < 720)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const sidebarStyles = {
    width: isMobile ? '100%' : isCompact ? '30%' : '20%',
    height: isMobile ? 'auto' : 'auto',
    backgroundColor: colors.secondaryBg,
    borderRight: isMobile ? 'none' : `1px solid ${colors.primaryBorder}`,
    borderBottom: isMobile ? `1px solid ${colors.primaryBorder}` : 'none',
    padding: isMobile ? theme.spacing.sm : isCompact ? theme.spacing.xs : theme.spacing.sm,
    overflowY: 'auto' as const,
    minHeight: 0,
    maxHeight: isMobile ? '180px' : 'none',
  }

  const folderItemStyles = {
    base: {
      paddingTop: isMobile ? '6px' : (isCompact ? theme.spacing.xs : theme.spacing.sm),
      paddingBottom: isMobile ? '6px' : (isCompact ? theme.spacing.xs : theme.spacing.sm),
      paddingRight: theme.spacing.sm,
      borderRadius: theme.borderRadius.sm,
      cursor: 'pointer',
      color: colors.primaryText,
      marginBottom: isMobile ? '1px' : (isCompact ? '4px' : theme.spacing.xs),
      fontSize: isMobile ? '12px' : (isCompact ? theme.fontSize.xs : theme.fontSize.sm),
      transition: theme.transitions.default,
    },
    active: {
      backgroundColor: colors.folderSelectedBg,
      color: colors.folderSelectedText,
    },
    hover: {
      backgroundColor: colors.secondaryBg,
      color: colors.primaryText,
    },
  }

  const tableStyles = {
    width: '100%',
    minWidth: isMobile ? 'auto' : isCompact ? '400px' : '600px',
    fontSize: isCompact ? theme.fontSize.xs : theme.fontSize.sm,
    borderCollapse: 'collapse' as const,
    tableLayout: 'fixed' as const,
  }

  const cellStyles = {
    padding: `${isCompact ? theme.spacing.sm : theme.spacing.md} ${theme.spacing.sm}`,
    borderBottom: `1px dashed ${colors.primaryBorder}`,
  }

  const headerCellStyles = {
    ...cellStyles,
    fontWeight: 'normal' as const,
    color: colors.secondaryText,
    paddingBottom: isCompact ? theme.spacing.xs : theme.spacing.sm,
  }

  return (
    <Container style={{ width: '100%' }}>
      <div
        ref={containerRef}
        onClick={() => onActivateWindow?.()}
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        {/* Main layout */}
        <div
          style={{
            display: 'flex',
            flex: 1,
            overflow: 'hidden',
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          {/* Sidebar - always visible */}
          <div style={sidebarStyles}>
            {folders.map((folder) => renderFolder(folder))}
          </div>

          {/* Main content */}
          <div
            style={{
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Path header - hide on mobile since folder tree is visible */}
            {!isMobile && (
              <div
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  padding: `${isCompact ? theme.spacing.sm : theme.spacing.md} ${isCompact ? theme.spacing.md : theme.spacing.md}`,
                  backgroundColor: colors.secondaryBg,
                  borderBottom: `1px solid ${colors.primaryBorder}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  minHeight: isCompact ? '36px' : '48px',
                }}
              >
                <div
                  style={{
                    color: colors.secondaryText,
                    fontSize: isCompact ? theme.fontSize.xs : theme.fontSize.sm,
                    display: 'flex',
                    alignItems: 'center',
                    gap: isCompact ? '4px' : theme.spacing.xs,
                  }}
                >
                  <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('📂')}</span>
                  <span style={{ fontFamily: theme.fontFamily.mono }}>
                    {currentFolder}
                  </span>
                </div>
                {/* Font Selector - only show in Themes folder */}
                {currentFolder === '/Themes' && (
                  <GoogleFontSelector isCompact={isCompact} />
                )}
              </div>
            )}
            <div
              style={{
                padding: isMobile ? theme.spacing.sm : `${theme.spacing.lg} ${theme.spacing.xl}`,
                overflowX: isMobile ? 'hidden' : 'auto',
                flex: 1,
              }}
            >
              {isLoading ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '128px',
                  }}
                >
                  <Message variant="loading">
                    {t('fileExplorer.loadingFiles')}
                  </Message>
                </div>
              ) : isMobile && currentFolder === '/' ? (
                // Mobile root: blank area, folders are in sidebar
                <div style={{ flex: 1 }} />
              ) : getFolderContents(currentFolder).length === 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '256px',
                  }}
                >
                  <pre
                    style={{
                      color: colors.emptyStateText,
                      textAlign: 'center',
                      marginBottom: theme.spacing.lg,
                    }}
                  >
                    {getAsciiArt()}
                  </pre>
                  <Message variant="info">
                    {t('fileExplorer.selectFolder')}
                  </Message>
                </div>
              ) : isMobile ? (
                // Mobile: Card-based layout
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {getFolderContents(currentFolder).map((item) => (
                    <div
                      key={item.id}
                      onClick={() => handleOpenItem(item)}
                      style={{
                        padding: '12px',
                        backgroundColor: colors.secondaryBg,
                        borderRadius: '8px',
                        border: `1px solid ${colors.primaryBorder}`,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        transition: 'background-color 0.15s ease',
                      }}
                    >
                      {/* Icon */}
                      <div style={{ 
                        fontSize: '24px', 
                        width: '40px', 
                        height: '40px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        backgroundColor: colors.primaryBg,
                        borderRadius: '8px',
                        flexShrink: 0,
                        fontFamily: "'Noto Emoji', sans-serif",
                      }}>
                        {normalizeEmojis(item.icon)}
                      </div>
                      
                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          color: colors.primaryText, 
                          fontWeight: 500,
                          fontSize: '14px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {item.name}
                        </div>
                        <div style={{ 
                          color: colors.tertiaryText, 
                          fontSize: '11px',
                          marginTop: '2px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          flexWrap: 'wrap',
                        }}>
                          <span style={{ textTransform: 'uppercase' }}>{item.type}</span>
                          {item.type !== 'folder' && (
                            <>
                              <span>•</span>
                              {'importedFromShareId' in item && item.importedFromShareId ? (
                                <span style={{ color: '#f59e0b', fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('📥')}</span>
                              ) : 'isListed' in item && item.isListed ? (
                                <span style={{ color: colors.primaryAccent, fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('💰')}</span>
                              ) : 'shareId' in item && item.shareId ? (
                                <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('🔗')}</span>
                              ) : (
                                <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('🔒')}</span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      {!item.id.startsWith('builtin-') && item.type !== 'folder' && (
                        <div style={{ 
                          display: 'flex', 
                          gap: '4px',
                          flexShrink: 0,
                        }}>
                          {/* Share/View link button */}
                          <button
                            onClick={(e) => handleShareClick(item, e)}
                            style={{
                              width: '36px',
                              height: '36px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: colors.primaryBg,
                              border: `1px solid ${colors.primaryBorder}`,
                              borderRadius: '8px',
                              cursor: 'pointer',
                            }}
                            title={'shareId' in item && item.shareId ? "View share link" : "Share"}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.primaryText} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              {'shareId' in item && item.shareId ? (
                                <>
                                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                                </>
                              ) : (
                                <>
                                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                                </>
                              )}
                            </svg>
                          </button>
                          {/* Unshare button - only for shared items */}
                          {'shareId' in item && item.shareId && (
                            <button
                              onClick={(e) => handleUnshareClick(item, e)}
                              style={{
                                width: '36px',
                                height: '36px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: colors.primaryBg,
                                border: `1px solid ${colors.primaryBorder}`,
                                borderRadius: '8px',
                                cursor: 'pointer',
                              }}
                              title="Make private"
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.primaryText} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 9.9-1" />
                              </svg>
                            </button>
                          )}
                          {/* Delete button */}
                          <button
                            onClick={(e) => handleDeleteClick(item, e)}
                            style={{
                              width: '36px',
                              height: '36px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: colors.primaryBg,
                              border: `1px solid ${colors.primaryBorder}`,
                              borderRadius: '8px',
                              cursor: 'pointer',
                            }}
                            title="Delete"
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.primaryText} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 6h18" />
                              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                              <line x1="10" y1="11" x2="10" y2="17" />
                              <line x1="14" y1="11" x2="14" y2="17" />
                            </svg>
                          </button>
                        </div>
                      )}
                      
                      {/* Folder arrow */}
                      {item.type === 'folder' && (
                        <div style={{ 
                          color: colors.tertiaryText, 
                          fontSize: '18px',
                          flexShrink: 0,
                        }}>
                          ›
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <table style={tableStyles}>
                  <thead>
                    <tr
                      style={{
                        textAlign: 'left',
                      }}
                    >
                      <th
                        style={{
                          ...headerCellStyles,
                          width: isCompact ? '100%' : '250px',
                          whiteSpace: 'nowrap' as const,
                        }}
                      >
                        {t('fileExplorer.table.name')}
                      </th>
                      {!isMobile && !isCompact && (
                        <th
                          style={{
                            ...headerCellStyles,
                            width: '170px',
                            whiteSpace: 'nowrap' as const,
                          }}
                        >
                          {t('fileExplorer.table.lastEdited')}
                        </th>
                      )}
                      <th
                        style={{
                          ...headerCellStyles,
                          width: isCompact ? '80px' : '100px',
                          textAlign: 'center' as const,
                        }}
                      >
                        {t('fileExplorer.table.actions')}
                        <HelpIcon text={t('help.fileExplorer.actionsColumn')} position="bottom" />
                      </th>
                      {!isMobile && !isCompact && (
                        <th
                          style={{
                            ...headerCellStyles,
                            width: '120px',
                            whiteSpace: 'nowrap' as const,
                          }}
                          data-marketplace-status
                        >
                          Status
                          <HelpIcon text={t('help.fileExplorer.statusColumn')} position="bottom" />
                        </th>
                      )}
                      {!isMobile && !isCompact && (
                        <th
                          style={{
                            ...headerCellStyles,
                            width: '80px',
                            whiteSpace: 'nowrap' as const,
                          }}
                        >
                          Price
                          <HelpIcon text={t('help.fileExplorer.priceColumn')} position="bottom" />
                        </th>
                      )}
                      {!isMobile && !isCompact && (
                        <th
                          style={{
                            ...headerCellStyles,
                            width: '180px',
                            whiteSpace: 'nowrap' as const,
                          }}
                        >
                          Stats
                          <HelpIcon text={t('help.fileExplorer.statsColumn')} position="bottom" />
                        </th>
                      )}
                      <th
                        style={{
                          ...headerCellStyles,
                          width: isCompact ? '60px' : '80px',
                          whiteSpace: 'nowrap' as const,
                        }}
                      >
                        {t('fileExplorer.table.type')}
                      </th>
                      {!isMobile && !isCompact && (
                        <th
                          style={{
                            ...headerCellStyles,
                            width: '80px',
                            whiteSpace: 'nowrap' as const,
                          }}
                        >
                          {t('fileExplorer.table.size')}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {getFolderContents(currentFolder).map((item) => (
                      <tr
                        key={item.id}
                        style={{
                          cursor: 'pointer',
                          transition: theme.transitions.default,
                        }}
                        onClick={() => handleOpenItem(item)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor =
                            colors.secondaryBg
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent'
                        }}
                      >
                        <td
                          style={{
                            ...cellStyles,
                            color: colors.primaryText,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <span style={{ marginRight: theme.spacing.sm, fontFamily: "'Noto Emoji', sans-serif" }}>
                            {normalizeEmojis(item.icon)}
                          </span>
                          <span
                            style={{
                              display: 'inline-block',
                              verticalAlign: 'bottom',
                              maxWidth: 'calc(100% - 30px)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.name}
                          </span>
                          {isCompact && item.type !== 'folder' && (
                            <div
                              style={{
                                marginTop: '2px',
                                color: colors.tertiaryText,
                                fontSize: theme.fontSize.xs,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {'importedFromShareId' in item && item.importedFromShareId ? (
                                <span style={{ color: '#f59e0b', fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('📥')} Imported</span>
                              ) : 'isListed' in item && item.isListed ? (
                                <span style={{ color: colors.primaryAccent, fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('💰')} Listed</span>
                              ) : 'shareId' in item && item.shareId ? (
                                <span style={{ color: colors.secondaryText, fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('🔗')} Shared</span>
                              ) : (
                                <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('🔒')} Private</span>
                              )}
                              {'  ·  '}
                              {item.updatedAt !== '-'
                                ? formatDateTime(item.updatedAt)
                                : '-'}
                              {'  ·  '}
                              {item.size}
                            </div>
                          )}
                        </td>
                        {!isMobile && !isCompact && (
                          <td
                            style={{
                              ...cellStyles,
                              color: colors.tertiaryText,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {item.updatedAt !== '-'
                              ? formatDateTime(item.updatedAt)
                              : '-'}
                          </td>
                        )}
                        <td
                          style={{
                            ...cellStyles,
                            textAlign: 'center' as const,
                          }}
                        >
                          {!item.id.startsWith('builtin-') &&
                          item.type !== 'folder' ? (
                            <div
                              style={{
                                display: 'flex',
                                gap: theme.spacing.xs,
                                justifyContent: 'center',
                                flexWrap: 'wrap',
                              }}
                            >
                              {/* Share/Copy Link/Unshare Buttons */}
                              {'shareId' in item && item.shareId ? (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      onActivateWindow?.()
                                      if ('shareId' in item && item.shareId) {
                                        navigator.clipboard.writeText(
                                          `${window.location.origin}/${item.type === 'html' ? 'artifacts' : item.type === 'theme' ? 'themes' : 'identities'}/shared/${item.shareId}`
                                        )
                                        setCopiedItemId(item.id)
                                        setTimeout(() => setCopiedItemId(null), 2000)
                                      }
                                    }}
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      cursor: 'pointer',
                                      fontSize: theme.fontSize.xs,
                                      padding: theme.spacing.xs,
                                      opacity: 0.7,
                                      color: colors.primaryText,
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                    title="Copy public share link to clipboard"
                                  >
                                    {isCompact ? (
                                      copiedItemId === item.id ? '✓' : '📎'
                                    ) : copiedItemId === item.id ? (
                                      '[Copied!]'
                                    ) : (
                                      <span style={{ fontSize: '14px' }}>📎</span>
                                    )}
                                  </button>
                                  <a
                                    href={getTweetUrl(item, getShareUrl(item))}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      cursor: 'pointer',
                                      fontSize: theme.fontSize.xs,
                                      padding: theme.spacing.xs,
                                      opacity: 0.7,
                                      color: colors.primaryText,
                                      display: 'flex',
                                      alignItems: 'center',
                                      textDecoration: 'none',
                                    }}
                                    title="Share on Twitter"
                                  >
                                    <svg
                                      viewBox="0 0 24 24"
                                      width={isCompact ? 12 : 14}
                                      height={isCompact ? 12 : 14}
                                      fill="currentColor"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.84 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                                    </svg>
                                  </a>
                                  <button
                                    onClick={(e) => handleUnshareClick(item, e)}
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      cursor: 'pointer',
                                      fontSize: theme.fontSize.xs,
                                      padding: theme.spacing.xs,
                                      opacity: 0.7,
                                      color: colors.primaryText,
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                    title="Remove public share link and make this item private"
                                  >
                                    {isCompact ? '🔓' : '[Unshare]'}
                                    <HelpIcon text={t('help.fileExplorer.unshareAction')} position="top" />
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={(e) => handleShareClick(item, e)}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: isCompact
                                      ? theme.fontSize.sm
                                      : theme.fontSize.xs,
                                    padding: isCompact ? '2px' : theme.spacing.xs,
                                    opacity: 0.7,
                                    color: colors.primaryText,
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                  title="Create a public share link"
                                >
                                  {isCompact ? <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('🔗')}</span> : `[${t('fileExplorer.share')}]`}
                                  <HelpIcon text={t('help.fileExplorer.shareAction')} position="top" />
                                </button>
                              )}

                              {/* Delete Button */}
                              <button
                                onClick={(e) => handleDeleteClick(item, e)}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: isCompact
                                    ? theme.fontSize.sm
                                    : theme.fontSize.xs,
                                  padding: isCompact ? '2px' : theme.spacing.xs,
                                  opacity: 0.7,
                                  color: colors.primaryText,
                                }}
                                title={t('fileExplorer.delete')}
                              >
                                {isCompact ? '×' : '[Delete]'}
                              </button>
                            </div>
                          ) : (
                            <span style={{ color: colors.tertiaryText }}>
                              —
                            </span>
                          )}
                        </td>
                        {!isMobile && !isCompact && (
                          <td
                            style={{
                              ...cellStyles,
                              color: colors.tertiaryText,
                              whiteSpace: 'nowrap',
                              fontSize: theme.fontSize.sm,
                            }}
                            title={
                              item.type === 'folder'
                                ? ''
                                : 'importedFromShareId' in item && item.importedFromShareId
                                ? 'This item was imported from elsewhere. You can use it, but cannot list it for sale.'
                                : 'isListed' in item && item.isListed
                                ? 'This item is currently listed for sale on the marketplace'
                                : 'shareId' in item && item.shareId
                                ? 'This item has a public share link that others can view and import'
                                : 'This item is private - only you can see it'
                            }
                          >
                            {item.type === 'folder' ? (
                              <span style={{ color: colors.tertiaryText }}>—</span>
                            ) : 'importedFromShareId' in item && item.importedFromShareId ? (
                              <span style={{ color: '#f59e0b', fontFamily: "'Noto Emoji', sans-serif" }}>
                                {normalizeEmojis('📥')} Imported
                              </span>
                            ) : 'isListed' in item && item.isListed ? (
                              <span style={{ color: colors.primaryAccent, fontFamily: "'Noto Emoji', sans-serif" }}>
                                {normalizeEmojis('💰')} Listed
                              </span>
                            ) : 'shareId' in item && item.shareId ? (
                              <span style={{ color: colors.secondaryText, fontFamily: "'Noto Emoji', sans-serif" }}>
                                {normalizeEmojis('🔗')} Shared
                              </span>
                            ) : (
                              <span style={{ color: colors.tertiaryText, fontFamily: "'Noto Emoji', sans-serif" }}>
                                {normalizeEmojis('🔒')} Private
                              </span>
                            )}
                          </td>
                        )}
                        {!isMobile && !isCompact && (
                          <td
                            style={{
                              ...cellStyles,
                              color: colors.tertiaryText,
                              whiteSpace: 'nowrap',
                              fontSize: theme.fontSize.sm,
                            }}
                          >
                            {item.type !== 'folder' && 'isListed' in item && item.isListed && 'price' in item && item.price
                              ? `$${item.price.toFixed(2)}`
                              : '-'}
                          </td>
                        )}
                        {!isMobile && !isCompact && (
                          <td
                            style={{
                              ...cellStyles,
                              color: colors.tertiaryText,
                              whiteSpace: 'nowrap',
                              fontSize: theme.fontSize.xs,
                            }}
                          >
                            {item.type !== 'folder' && 'isListed' in item && item.isListed ? (
                              <span>
                                💳 {'purchaseCount' in item ? item.purchaseCount || 0 : 0} •{' '}
                                💵 ${(('price' in item ? item.price || 0 : 0) * ('purchaseCount' in item ? item.purchaseCount || 0 : 0)).toFixed(2)}
                              </span>
                            ) : item.type !== 'folder' && 'shareId' in item && item.shareId ? (
                              <span>
                                👁️ {'viewCount' in item ? item.viewCount || 0 : 0} •{' '}
                                📥 {'importCount' in item ? item.importCount || 0 : 0}
                              </span>
                            ) : (
                              '-'
                            )}
                          </td>
                        )}
                        <td
                          style={{
                            ...cellStyles,
                            color: colors.tertiaryText,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {item.type}
                        </td>
                        {!isMobile && !isCompact && (
                          <td
                            style={{
                              ...cellStyles,
                              color: colors.tertiaryText,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {item.size}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Info banner - only visible when help is enabled */}
        {helpEnabled && (
          <div
            style={{
              padding: `${theme.spacing.md} ${theme.spacing.lg}`,
              backgroundColor: colors.primaryBg,
              borderTop: `1px solid ${colors.primaryBorder}`,
              color: colors.secondaryText,
              fontSize: theme.fontSize.sm,
              textAlign: 'center',
              lineHeight: '1.6',
            }}
          >
            <span style={{ color: colors.primaryText, fontWeight: 600 }}>
              <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('📁')}</span> {t('fileExplorer.title')}
            </span>
            {' • '}
            {t('fileExplorer.emptyState')}
          </div>
        )}

        {/* Status bar - compact on mobile */}
        <div
          style={{
            padding: isMobile ? `${theme.spacing.xs} ${theme.spacing.sm}` : `${theme.spacing.sm} ${theme.spacing.lg}`,
            backgroundColor: colors.secondaryBg,
            borderTop: `1px solid ${colors.primaryBorder}`,
            color: colors.secondaryText,
            fontSize: theme.fontSize.xs,
            display: 'flex',
            gap: isMobile ? theme.spacing.sm : theme.spacing.lg,
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {isMobile ? (
            // Compact mobile version - icons with small text
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flex: 1, fontSize: '10px' }}>
              <span title="Private" style={{ display: 'flex', alignItems: 'center', gap: '2px', fontFamily: theme.fontFamily.mono }}><span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('🔒')}</span> Private</span>
              <span title="Shared" style={{ display: 'flex', alignItems: 'center', gap: '2px', fontFamily: theme.fontFamily.mono }}><span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('🔗')}</span> Shared</span>
              <span title="Listed" style={{ display: 'flex', alignItems: 'center', gap: '2px', fontFamily: theme.fontFamily.mono }}><span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('💰')}</span> Listed</span>
              <span title="Imported" style={{ display: 'flex', alignItems: 'center', gap: '2px', fontFamily: theme.fontFamily.mono }}><span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('📥')}</span> Imported</span>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: theme.spacing.lg, flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
              <span style={{ fontWeight: 600, color: colors.primaryText, display: 'flex', alignItems: 'center' }}>
                {t('fileExplorer.statusGuide.title')}
                <HelpIcon text={t('help.fileExplorer.statusGuide')} position="top" />
              </span>
              <span title="Items you created - only visible to you">
                <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('🔒')}</span> {t('fileExplorer.statusGuide.private')}
              </span>
              <span title="Items with a public link - others can view and import">
                <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('🔗')}</span> {t('fileExplorer.statusGuide.shared')}
              </span>
              <span title="Items for sale on the marketplace">
                <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('💰')}</span> {t('fileExplorer.statusGuide.listed')}
              </span>
              <span title="Items imported from others - you cannot sell these">
                <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('📥')}</span> {t('fileExplorer.statusGuide.imported')}
              </span>
            </div>
          )}
          <div style={{ marginLeft: 'auto' }}>
            <HelpToggle />
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t('fileExplorer.deleteArtifact')}
        message={t('fileExplorer.deleteConfirm', { name: itemToDelete?.name })}
        confirmText={t('fileExplorer.delete')}
        cancelText={t('fileExplorer.cancel')}
        variant="danger"
      />

      {/* Share modal */}
      {shareModalOpen && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            padding: isMobile ? '16px' : '24px',
            fontFamily: "'Departure Mono', monospace",
          }}
          onClick={handleCloseShareModal}
        >
          <div
            style={{
              backgroundColor: colors.primaryBg,
              border: `1px solid ${colors.primaryBorder}`,
              borderRadius: '12px',
              padding: isMobile ? '16px' : '24px',
              maxWidth: isMobile ? '100%' : '600px',
              width: '100%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              fontFamily: "'Departure Mono', monospace",
              boxSizing: 'border-box',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              style={{
                fontSize: '14px',
                fontWeight: 'bold',
                color: colors.primaryText,
                marginBottom: '10px',
                fontFamily: "'Departure Mono', monospace",
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={itemToShare?.name}
            >
              {t('fileExplorer.shareItem', { name: itemToShare?.name })}
            </h2>

            {isSharing ? (
              <Message variant="loading">Generating share link...</Message>
            ) : shareUrl ? (
              <>
                <p
                  style={{
                    fontSize: '12px',
                    color: colors.secondaryText,
                    marginBottom: '12px',
                    fontFamily: "'Departure Mono', monospace",
                  }}
                >
                  Anyone with this link can view and import this{' '}
                  {itemToShare?.type === 'theme' ? 'theme' : 'artifact'}:
                </p>
                <div
                  style={{
                    padding: '8px',
                    backgroundColor: colors.secondaryBg,
                    border: `1px solid ${colors.primaryBorder}`,
                    borderRadius: '8px',
                    marginBottom: '16px',
                  }}
                >
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      backgroundColor: colors.primaryBg,
                      border: `1px solid ${colors.primaryBorder}`,
                      borderRadius: '6px',
                      color: colors.primaryText,
                      fontSize: '11px',
                      fontFamily: "'Departure Mono', monospace",
                    }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                  <button
                    onClick={handleCopyShareLink}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      backgroundColor: colors.primaryAccent,
                      color: colors.primaryBg,
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: "'Departure Mono', monospace",
                    }}
                  >
                    {shareCopyFeedback ? 'Copied!' : 'Copy Link'}
                  </button>
                  <a
                    href={itemToShare ? getTweetUrl(itemToShare, shareUrl) : '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      backgroundColor: '#1DA1F2',
                      color: 'white',
                      border: 'none',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 600,
                      fontFamily: "'Departure Mono', monospace",
                    }}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width={14}
                      height={14}
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.84 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                    </svg>
                    Tweet
                  </a>
                  <button
                    onClick={handleCloseShareModal}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      backgroundColor: 'transparent',
                      color: colors.primaryText,
                      border: `1px solid ${colors.primaryBorder}`,
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: "'Departure Mono', monospace",
                    }}
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <Message variant="error">Failed to create share link</Message>
            )}
          </div>
        </div>,
        document.body
      )}
      
      {/* Mobile notice for artifact viewer */}
      {showMobileToast && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              backgroundColor: colors.primaryBg,
              border: `1px solid ${colors.primaryBorder}`,
              borderRadius: '8px',
              padding: '16px 24px',
              fontFamily: "'Departure Mono', monospace",
              fontSize: '13px',
              fontStyle: 'italic',
              color: colors.secondaryText,
              textAlign: 'center',
              letterSpacing: '0.3px',
              animation: 'fadeInOut 3s ease-in-out forwards',
            }}
          >
            artifacts viewable on desktop only
          </div>
        </div>,
        document.body
      )}
    </Container>
  )
}

export default function FileExplorer(props: FileExplorerProps) {
  return (
    <FileExplorerHelpProvider>
      <FileExplorerContent {...props} />
    </FileExplorerHelpProvider>
  )
}
